import Vue from 'vue';
import { http } from '../../http';
import AccessLevel from '../../../core/AccessLevel';
import { SESSION_TIMER_UPDATE, SESSION_DURATION_FACTOR, SESSION_EXPIRING_DEFAULT_TIMEOUT } from '../../helpers/constants'
import moment from 'moment'

export default {
    state: {
        user: null,
        owner: null,
        generalPermissions: {},
        targetPermissions: {},
        loadingPermission: false,
        currentTime: moment().format('YYYY-MM-DD HH:mm:ss'),
        currentTimeTimeout: null,
    },
    
    mutations: {
        setUser(state, user) {
            state.user = user;
        },

        setOwner(state, owner) {
            state.owner = owner;
        },

        setFreeMonths(state, freeMonths) {
            if (state.user) {
                state.user.bank = freeMonths;
            }

            if (state.owner) {
                state.owner.bank = freeMonths;
            }
        },

        setTargetPermission(state, { name, level }) {
            Vue.set(state.targetPermissions, name, level);
        },

        setGeneralPermission(state, { name, level }) {
            Vue.set(state.generalPermissions, name, level);
        },

        setLoadingPermission(state, value = true) {
            state.loadingPermission = value;
        },

        setLastActivity(state, lastActivity) {
            if (state.user) {
                const currentTime = moment().utc().format('YYYY-MM-DD HH:mm:ss')
                if (!lastActivity) {
                    lastActivity = currentTime
                }
                state.currentTime = currentTime
                state.user.last_activity = lastActivity
            }
        },

        setSessionLifetime(state, lifetime) {
            if (state.user) {
                state.user.session_lifetime = lifetime
            }
        },

        updateCurrentTime(state, time = moment().utc()) {
            state.currentTime = time.format('YYYY-MM-DD HH:mm:ss')
        },

        setCurrentTimeTimeout(state, interval) {
            clearInterval(state.currentTimeTimeout)
            state.currentTimeTimeout = interval
        },
    },

    getters: {
        isLoadingPermission: (state) => {
            return state.loadingPermission;
        },

        generalPermissionForUser: (state, getters) => (permissionName, user = null) => {
            let userId = null;
            let rawPermissionName = permissionName;

            if (user) {
                userId = typeof user === 'object' ? user.id : parseInt(user);
                permissionName = `user-${userId}-${rawPermissionName}`;
            }

            if (!state.generalPermissions.hasOwnProperty(permissionName)) {
                Vue.set(state.generalPermissions, permissionName, AccessLevel.None);
                Vue.set(state, 'loadingPermission', true);

                http.get('/app/authorize-general', { params: { type: rawPermissionName, user_id: userId || null } })
                    .then(({ data }) => {
                        Vue.set(state, 'loadingPermission', false);
                        Vue.set(state.generalPermissions, data.name, data.level);
                    })
                    .catch((error) => {
                        Vue.set(state, 'loadingPermission', false);
                        Vue.set(state.generalPermissions, permissionName, AccessLevel.None);
                    });
            }

            return state.generalPermissions[permissionName];
        },

        isGeneralPermission: (state, getters) => (permissionName) => {
            return state.generalPermissions && state.generalPermissions.hasOwnProperty(permissionName);
        },

        can: (state, getters) => (permissionType, permissionTarget = null, permissionModel = null) => {
            return getters['userCan'](null, permissionType, permissionTarget, permissionModel);
        },

        userCan: (state, getters) => (user, permissionType, permissionTarget = null, permissionModel = null) => {
            // so... the `permissionType` could be either 'see', 'edit', 'see-xxx' or 'edit-xxx'

            let userId = user ? (typeof user === 'object' ? user.id : parseInt(user)) : null;

            if (userId && userId === state.user.id) {
                // the user being checked is the same as currently logged in user
                userId = null;
                user = null;
            }

            let isOwner = Boolean((!userId && getters.isOwner) || (userId && userId === state.owner.id));

            if (typeof permissionType !== 'string') {
                throw new Error('Invalid permission request. The `permissionType` parameter must be a string.');
            }

            if (permissionTarget && (typeof permissionTarget === 'object' || typeof permissionTarget === 'number')) {
                permissionModel = permissionTarget;
            }

            if (permissionType.includes('-')) {
                [ permissionType, permissionTarget ] = permissionType.split('-', 2);
            }

            permissionType = permissionType.toLowerCase();
            permissionTarget = permissionTarget.toLowerCase();

            let minimumRequiredLevel = AccessLevel.None;

            if (permissionType.startsWith('see')) {
                minimumRequiredLevel = AccessLevel.Show;
            } else if (permissionType.startsWith('edit')) {
                minimumRequiredLevel = AccessLevel.Edit;
            } else {
                throw new Error('Invalid permission request: "' + permissionType + '".');
            }

            if (isOwner) {
                // owners can see and edit everything
                return true;
            }

            if (getters['isGeneralPermission'](permissionTarget)) {
                return getters['generalPermissionForUser'](permissionTarget, user) >= minimumRequiredLevel;
            }

            // if not a general permission, this could be a resource!

            if (!user && permissionModel && typeof permissionModel === 'object' && permissionModel[`can_${permissionType}`] !== undefined) {
                // this should only apply to `$can` method where the user is the current user by default.
                // that's because the loaded resources and their `can_see` or `can_edit` attributes are scoped
                // to the currently logged in user.
                return Boolean(permissionModel[`can_${permissionType}`]);
            }

            let permissionModelId = permissionModel && typeof permissionModel === 'object' ? permissionModel.id : parseInt(permissionModel);
            let targetPermissionIdentifier = `${permissionTarget}-${permissionModelId}`;

            if (userId) {
                targetPermissionIdentifier = `user-${userId}-${targetPermissionIdentifier}`;
            }

            // as a last resort, let's check against the backend
            if (state.targetPermissions[targetPermissionIdentifier] === undefined) {
                // we now set it to "None" while we fetch the actual result from the backend
                Vue.set(state.targetPermissions, targetPermissionIdentifier, AccessLevel.None);

                if (permissionModelId !== undefined && permissionModelId !== null) {
                    state.loadingPermission = true;
    
                    http.get('/app/authorize-resource', { params: { target_type: permissionTarget, target_id: permissionModelId || null, user_id: userId } })
                        .then(({ data }) => {
                            state.loadingPermission = false;
                            Vue.set(state.targetPermissions, data.name, data.level);
                        })
                        .catch((error) => {
                            state.loadingPermission = false;
                            Vue.set(state.targetPermissions, targetPermissionIdentifier, AccessLevel.None);
                        });
                }
            }

            return state.targetPermissions[targetPermissionIdentifier] >= minimumRequiredLevel;
        },

        isOwner: (state, getters) => {
            return state.user && state.owner && state.user.id === state.owner.id;
        },

        lifetimeInterval: (state) => state.user ? moment.duration(state.user.session_lifetime).asMinutes() : null,

        sessionExpiringWarningTimeout: (state, getters) => {
            return Math.ceil(Math.min(SESSION_EXPIRING_DEFAULT_TIMEOUT, getters.lifetimeInterval * SESSION_DURATION_FACTOR))
        },

        remainingSessionMinutes: (state, getters) => moment(state.user.last_activity).add(getters.lifetimeInterval, 'minutes').diff(moment(state.currentTime), 'minutes')
    },

    actions: {
        load({ commit }) {
            http.get('/app/user').then(({data}) => {
                commit('setUser', data.user);
                commit('setOwner', data.owner);
            }).catch(error => {
                commit('setUser', null);
                commit('setOwner', null);
            });
        },

        loadOwner({ commit }) {
            http.get('/app/owner').then(({data}) => {
                commit('setOwner', data);
            }).catch(error => {
                commit('setOwner', null);
            });
        },

        giveResourceAccess({ commit }, { user, resource_type, resource_id, level }) {
            let userId = user && typeof user === 'object' ? user.id : (user ? parseInt(user) : null);

            if (![0, 5, 10].includes(level)) {
                throw new Error('Incorrect access level given. The attribute `level` must only contain the numbers 0, 5 or 10.');
            }

            http.post(`/app/authorize-resource`, { target_type: resource_type, target_id: resource_id, user_id: userId, level: level })
                .then(({ data }) => {
                    commit('setTargetPermission', data);

                    if (data.success) {
                        toastr.success(data.success);
                    }
                }).catch(() => {});
        },

        recordLastActivity({ commit, dispatch }) {
            // Record last user activity and restart the session interval
            commit('setLastActivity')
            dispatch('startSessionTimeout')
        },

        startSessionTimeout({ commit }) {
            // Clear the previous interval
            commit('setCurrentTimeTimeout', null)

            const interval = setInterval(() => {
                commit('updateCurrentTime')
            }, SESSION_TIMER_UPDATE)

            commit('setCurrentTimeTimeout', interval)
        },

        fetchSessionDetails({ commit, getters }, params = { force: false }) {
            return new Promise((resolve) => {
                http.get('/ping', { params })
                    .then((response) => {
                        if (!params.force) {
                            commit('setLastActivity', response.data.last_activity_at)
                            commit('setSessionLifetime', response.data.session_lifetime)
                        }
                        resolve(getters.remainingSessionMinutes <= getters.sessionExpiringWarningTimeout)
                    })
                    .catch((error) => {
                        commit('setCurrentTimeTimeout', null)
                        window.location.href = '/login'
                    })
            })
        }
    }
};
